import { Card } from './Card';
import { UnfilledSummaryAppointment } from '../interfaces';
import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  AppointmentTypeEnum,
  useGetFhirEncountersByProviderQuery,
} from 'graphql/generated/remote-schema-hasura';

import { useAuth } from 'auth/context/AuthContext';
import { SIGNAL_CHANNELS, useChannelSignal } from 'hooks/useChannelSignal';
import { QueryContextType } from 'app/my-appointments/components/QueryContextType';
import Loader from '../../../components/loaderComponent';
import { Counter } from './CounterBadge';

import PaginateBlock from '../../../components/PaginateBlock';
import UnfilledSummaryAppointmentCard from './unfilledSummaryAppointments/UnfilledSummaryAppointmentCard';
import { componentIds } from 'utilities/constants';
import { useGetComponent } from 'hooks/useGetComponent';
import { EncounterStatuses } from 'graphql/generated/hasura';
import moment from 'moment';
import { FULL_DATE } from 'utilities/dateHelper';
import { useLocation } from 'react-router-dom';

const offsetParams = 'unfilledSummaryAppointmentsOffset';

type dataType = {
  appointments: UnfilledSummaryAppointment[];
  total: number;
};
type varsType = {
  limit: number;
  offset: number;
};

export interface UnfilledSummaryAppointmentsContextType
  extends QueryContextType<UnfilledSummaryAppointment, varsType> {
  loading: boolean;
  data: UnfilledSummaryAppointment[];
  total: number;
  variables: {
    limit: number;
    offset: number;
  };
  refetch: unknown;
  setVars: React.Dispatch<React.SetStateAction<varsType>>;
  allAmount?: number;
}

const UnfilledSummaryAppoinmentsContext =
  createContext<UnfilledSummaryAppointmentsContextType>({
    loading: false,
    appointments: [],
  } as unknown as UnfilledSummaryAppointmentsContextType);

export const useUnfilledSummaryAppointments = () => {
  const context = useContext(UnfilledSummaryAppoinmentsContext);
  if (!context?.setVars && process.env.REACT_APP_IS_DEBUG_MODE) {
    throw new Error(
      'useUnfilledSummaryAppointments must be used within a NotificationsProvider.',
    );
  }
  return context;
};

const UnfilledSummaryProviderAppointmentsProvider: React.FC<{
  children: React.ReactNode;
  offset?: number;
}> = ({ children, offset }) => {
  const { user: loggedUser } = useAuth();
  const [data, setData] = useState<dataType>({
    appointments: [],
    total: 0,
  });
  const [vars, setVars] = useState<varsType>({
    limit: 3,
    offset: offset || 0,
  });

  const { loading, refetch } = useGetFhirEncountersByProviderQuery({
    variables: {
      status: EncounterStatuses.InProgress,
      limit: vars.limit,
      offset: vars.offset,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFHIREncountersByProvider.total > 0) {
        const allUpcommingAppointments: UnfilledSummaryAppointment[] =
          data.getFHIREncountersByProvider.encounters.map((appointment) => ({
            appointmentId: appointment.appointmentCodexId as string,
            date: moment(appointment.appointmentDate || '', FULL_DATE)
              .utc(true)
              .toISOString(),
            patientId: appointment.patientCodexId || '',
            providerId: appointment.providerCodexId || '',
            encounterId: appointment.encounterId,
            appointmentType: (appointment.type ||
              AppointmentTypeEnum.Chat) as AppointmentTypeEnum,
            name: `${appointment.patientFirstName} ${appointment.patientLastName}`,
            status: appointment.status as EncounterStatuses,
          }));
        setData({
          appointments: allUpcommingAppointments,
          total: data.getFHIREncountersByProvider.total,
        });
      } else {
        setData({
          appointments: [],
          total: 0,
        });
      }
    },
  });

  useChannelSignal(
    (first: boolean) => {
      if (!first) {
        refetch();
      }
    },
    SIGNAL_CHANNELS.APPOINTMENTS,
    loggedUser?.uuid,
  );

  const value: UnfilledSummaryAppointmentsContextType = {
    data: data.appointments,
    variables: vars,
    total: data.total,
    refetch,
    loading,
    setVars,
  };

  return (
    <UnfilledSummaryAppoinmentsContext.Provider value={value}>
      {children}
    </UnfilledSummaryAppoinmentsContext.Provider>
  );
};

export const UnfilledSummaryAppointmentsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const offset = queryParams.get(offsetParams);
  return (
    <UnfilledSummaryProviderAppointmentsProvider offset={Number(offset) ?? 0}>
      {children}
    </UnfilledSummaryProviderAppointmentsProvider>
  );
};

export const UnfilledSummaryAppointments: React.FC<{
  handleNavigateCompleteSummaryModal: (appointmentId: string) => void;
  isDashboard?: boolean;
}> = ({ handleNavigateCompleteSummaryModal, isDashboard = false }) => {
  return (
    <UnfilledSummaryAppointmentsProvider>
      <UnfilledSummaryAppointmentsRenderer
        isDashboard={isDashboard}
        handleNavigateCompleteSummaryModal={handleNavigateCompleteSummaryModal}
      />
    </UnfilledSummaryAppointmentsProvider>
  );
};

const UnfilledSummaryAppointmentsRenderer: React.FC<{
  handleNavigateCompleteSummaryModal: (appointmentId: string) => void;
  isDashboard: boolean;
}> = ({ handleNavigateCompleteSummaryModal, isDashboard }) => {
  const { data: locale, loading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.UNFILLED_SUMMARY_APPOINTMENTS,
  });

  const {
    data: unfilledSummaryAppointments,
    loading: dataLoading,
    total,
    setVars,
    variables,
  } = useUnfilledSummaryAppointments();

  const hasShowPaginate = useMemo(
    () => total > variables.limit,
    [total, variables.limit],
  );

  const paginateSubtitle = useMemo(
    () =>
      `${locale?.showing} ${variables.offset + 1}-${Math.min(
        variables.offset + variables.limit,
        total,
      )} ${locale?.title} ${locale?.from} ${total}`,
    [locale, total, variables],
  );

  const setNewOffset = (offset: number) =>
    setVars((vars) => ({ ...vars, offset }));

  if (!locale || loading) {
    return null;
  }

  return unfilledSummaryAppointments?.length ? (
    <Card
      testId="UnfilledSummary-appointments-card"
      title={locale.title}
      alwaysExpanded
      hideMobileSubtitle
      subtitle={<Counter total={total} />}
      titleClassName="text-h3 text-neutral-800 font-semibold"
    >
      <div className="w-full text-left -mt-[10px] font-medium text-med-gray-3">
        {isDashboard ? locale?.dashboard : locale.subtitle}
      </div>

      {dataLoading ? (
        <div data-testid="UnfilledSummary-appointments-loading">
          <Loader />
        </div>
      ) : (
        unfilledSummaryAppointments.map((appointment) => (
          <UnfilledSummaryAppointmentCard
            appointment={appointment}
            key={appointment.appointmentId}
            handleNavigateCompleteSummaryModal={
              handleNavigateCompleteSummaryModal
            }
            locale={locale}
          />
        ))
      )}

      {hasShowPaginate && !dataLoading && (
        <div className="flex w-full max-desktop:flex-col gap-4 justify-between items-center mt-7">
          <p className="text-sm font-medium text-med-gray-3">
            {paginateSubtitle}
          </p>
          <PaginateBlock
            itemsPerPage={variables.limit}
            totalItems={total}
            setNewOffset={setNewOffset}
            offsetParams={offsetParams}
          />
        </div>
      )}
    </Card>
  ) : null;
};
